import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import * as api from '../services/api';

interface AuthContextType {
  isAdmin: boolean;
  token: string | null;
  login: (password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isAdmin: false,
  token: null,
  login: async () => false,
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('admin_token'));
  const isAdmin = token !== null;

  const login = useCallback(async (password: string): Promise<boolean> => {
    try {
      const res = await api.login(password);
      setToken(res.token);
      localStorage.setItem('admin_token', res.token);
      return true;
    } catch {
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    localStorage.removeItem('admin_token');
  }, []);

  // Listen for 401s from any authenticated request and force re-login.
  // The event is dispatched from services/api.ts, decoupling the API layer
  // from React context.
  useEffect(() => {
    const handler = () => {
      setToken(null);
      localStorage.removeItem('admin_token');
    };
    window.addEventListener('auth:unauthorized', handler);
    return () => window.removeEventListener('auth:unauthorized', handler);
  }, []);

  return (
    <AuthContext.Provider value={{ isAdmin, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
